import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, BellOff, MessageSquare, Reply } from "lucide-react";
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { useLocale } from "@/hooks/useLocale";
import { selectUser } from "@/store/slices/user.slice";
import {
  useGetChatConversationsInfiniteQuery,
  useGetChatUnreadSummaryQuery,
} from "@/services/(my-space)/api.chat";
import { MS_CHAT_PATHS } from "@/routes/my-space/routes.chat";
import type { IChatConversation } from "@/types/(my-space)/chat.interface";
import { cn } from "@/utils/cn";
import { ChatAvatar } from "@/pages/(home)/(my-space)/chat/_components/ChatAvatar";
import { QuickReplyBox } from "@/pages/(home)/(my-space)/chat/_components/QuickReplyBox";
import { openChat } from "@/components/layout/chat-windows/chat-windows-store";
import {
  conversationTime,
  conversationTitle,
  lastMessagePreview,
} from "@/pages/(home)/(my-space)/chat/_lib/chat-labels";
import { channelOf } from "@/pages/(home)/(my-space)/chat/_lib/channel-notices";

type InboxTab = "all" | "unread";
/** The list API's `tab` values (data, not copy). */
const INBOX_TABS: InboxTab[] = ["all", "unread"];

/**
 * Top-bar chat icon: like Notifications and Announcements, a box of the latest
 * chats (All / Unread) — a click opens one, Reply answers it right there, the
 * footer opens Chat. The badge counts conversations with unread messages; the
 * `/chat` socket keeps both current (it invalidates the same caches).
 */
export const ChatHeaderButton = () => {
  const { t } = useTranslation("chat");
  const user = useAppSelector(selectUser).user;
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  /** A reply is being typed: Esc closes that box, not the whole popover. */
  const replying = useRef(false);
  const { data } = useGetChatUnreadSummaryQuery(undefined, { skip: !user });
  const unread = data?.conversations ?? 0;
  const active = pathname === MS_CHAT_PATHS.ROOT || pathname.startsWith(`${MS_CHAT_PATHS.ROOT}/`);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("a11y.unread_chats", { count: unread })}
          title={t("page.title")}
          className={cn(
            "relative flex size-9 shrink-0 items-center justify-center rounded-sxs hover:bg-primary/10",
            (active || open) && "bg-primary/10",
          )}
        >
          <img
            src="/icons/interface-icons/shared/icon-3d-chat.png"
            alt=""
            draggable={false}
            className="size-5.5 object-contain"
          />
          {unread > 0 ? (
            <span className="absolute -top-2 -end-1.5 z-10 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[9px] leading-none font-bold text-danger-foreground">
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="relative me-2 w-[340px] overflow-hidden rounded-xl border-border/60 p-0 shadow-lg sm:w-[380px]"
          align="center"
          onEscapeKeyDown={(e) => {
            if (replying.current) e.preventDefault();
          }}
        >
          {open ? (
            <ChatInbox
              unread={unread}
              onClose={() => setOpen(false)}
              onReplyingChange={(v) => (replying.current = v)}
            />
          ) : null}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

const ChatInbox = ({
  unread,
  onClose,
  onReplyingChange,
}: {
  unread: number;
  onClose: () => void;
  onReplyingChange: (replying: boolean) => void;
}) => {
  const { t } = useTranslation("chat");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onChatPage =
    pathname === MS_CHAT_PATHS.ROOT || pathname.startsWith(`${MS_CHAT_PATHS.ROOT}/`);
  const myUserId = useAppSelector(selectUser).user?.id;
  const [tab, setTab] = useState<InboxTab>("all");
  /** The one chat whose reply box is open. */
  const [replyingId, setReplyingId] = useState<number | null>(null);
  useEffect(() => {
    onReplyingChange(replyingId != null);
    return () => onReplyingChange(false);
  }, [onReplyingChange, replyingId]);
  const query = useGetChatConversationsInfiniteQuery({
    tab,
    archived: false,
    search: "",
  });
  const chats = useMemo(() => query.data?.pages.flatMap((p) => p.data) ?? [], [query.data]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b bg-background px-4 py-3">
        <h3 className="text-[13px] font-semibold tracking-tight">{t("inbox.title")}</h3>
      </div>
      <div className="flex gap-1 border-b px-3 py-2">
        {INBOX_TABS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            aria-pressed={tab === value}
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors",
              tab === value
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {value === "all" ? t("list.tab_all") : t("list.tab_unread")}
            {value === "unread" && unread > 0 ? (
              <span className="inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-[8px] leading-none font-bold text-primary-foreground">
                {unread > 99 ? "99+" : unread}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div className="max-h-[380px] min-h-[380px] divide-y divide-border/50 overflow-y-auto">
        {query.isLoading ? (
          Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <Skeleton className="size-9 shrink-0 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-1/2 rounded" />
                <Skeleton className="h-2.5 w-3/4 rounded" />
              </div>
            </div>
          ))
        ) : chats.length === 0 ? (
          <div className="flex min-h-[380px] flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-muted">
              <MessageSquare className="size-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {tab === "unread" ? t("inbox.all_caught_up") : t("list.empty")}
            </p>
          </div>
        ) : (
          <>
            {chats.map((c) => (
              <InboxRow
                key={c.id}
                conversation={c}
                myUserId={myUserId}
                onOpen={() => {
                  onClose();
                  // A floating window where there's room; else the Chat page.
                  openChat(c.id, {
                    onChatPage: onChatPage,
                    goToChat: () => navigate(MS_CHAT_PATHS.CONVERSATION(c.id)),
                  });
                }}
                replying={replyingId === c.id}
                onReplying={(v) => setReplyingId(v ? c.id : null)}
              />
            ))}
            <div ref={sentinelRef} className="h-1" />
            {isFetchingNextPage ? (
              <div className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="size-9 shrink-0 rounded-full" />
                <Skeleton className="h-3 w-1/2 rounded" />
              </div>
            ) : null}
          </>
        )}
      </div>
      <div className="border-t bg-background">
        <button
          type="button"
          onClick={() => go(MS_CHAT_PATHS.ROOT)}
          className="group flex w-full items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-primary"
        >
          {t("inbox.open_chat")}
          <ArrowRight
            size={11}
            className="rtl-flip transition-transform group-hover:translate-x-0.5"
          />
        </button>
      </div>
    </div>
  );
};

const InboxRow = ({
  conversation: c,
  myUserId,
  onOpen,
  replying,
  onReplying,
}: {
  conversation: IChatConversation;
  myUserId: number | undefined;
  onOpen: () => void;
  replying: boolean;
  onReplying: (replying: boolean) => void;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const title = conversationTitle(t, c);
  const unread = c.unreadCount;
  const mentions = unread > 0 ? (c.unreadMentionCount ?? 0) : 0;
  return (
    <div className={cn("group/row", unread > 0 && "bg-primary/5")}>
      <div className="flex items-center transition-colors hover:bg-muted/60">
        <button
          type="button"
          onClick={onOpen}
          className="flex min-w-0 flex-1 items-center gap-3 py-3 ps-4 pe-1 text-start"
        >
          <ChatAvatar
            name={title}
            imageUrl={c.type === "direct" ? c.peer?.profileImage : c.imageUrl}
            kind={c.type}
            channel={channelOf(c)}
            size="md"
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  "truncate text-[13px] text-foreground",
                  unread > 0 ? "font-semibold" : "font-medium",
                )}
              >
                {title}
              </span>
              {c.isMuted ? (
                <BellOff
                  className="size-3 shrink-0 text-muted-foreground"
                  aria-label={t("a11y.muted")}
                />
              ) : null}
              <span
                className={cn(
                  "ms-auto shrink-0 text-[10px]",
                  unread > 0 ? "font-semibold text-primary" : "text-muted-foreground",
                )}
              >
                {conversationTime(t, formatDate, c.lastMessage?.createdAt ?? c.lastActivityAt)}
              </span>
            </span>
            <span className="flex items-center gap-2">
              <span
                dir="auto"
                className={cn(
                  "truncate text-xs",
                  unread > 0 ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {lastMessagePreview(t, c, myUserId)}
              </span>
              {mentions > 0 ? (
                <span
                  className="ms-auto flex size-4 shrink-0 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground"
                  aria-label={t("mentions.unread_mentions", {
                    count: mentions,
                  })}
                >
                  @
                </span>
              ) : null}
              {unread > 0 ? (
                <span
                  className={cn(
                    mentions > 0 ? "" : "ms-auto",
                    "flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[9px] font-bold",
                    c.isMuted
                      ? "bg-muted-foreground/60 text-background"
                      : "bg-primary text-primary-foreground",
                  )}
                  aria-label={t("a11y.unread_count", { count: unread })}
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              ) : null}
            </span>
          </span>
        </button>
        {c.canSend ? (
          <button
            type="button"
            onClick={() => onReplying(!replying)}
            aria-expanded={replying}
            aria-label={t("inbox.reply_to", { name: title })}
            title={t("popup.reply")}
            className={cn(
              "me-2 flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-opacity hover:bg-primary/10 hover:text-primary focus-visible:opacity-100",
              // Shown on hover where there is one; always on touch screens.
              replying
                ? "bg-primary/10 text-primary opacity-100"
                : "opacity-0 group-hover/row:opacity-100 [@media(hover:none)]:opacity-100",
            )}
          >
            <Reply className="size-4" />
          </button>
        ) : (
          <span className="w-3 shrink-0" />
        )}
      </div>
      {replying ? (
        <QuickReplyBox
          conversationId={c.id}
          name={title}
          onSent={() => onReplying(false)}
          onCancel={() => onReplying(false)}
          className="px-4 pb-3"
        />
      ) : null}
    </div>
  );
};
